import { bound } from "@frui.ts/helpers";
import { BusyWatcher, ScreenBase, watchBusy } from "@frui.ts/screens";
import { addError, getValidationMessage, removeError, validate } from "@frui.ts/validation";
import download from "downloadjs";
import toUpper from "lodash/toUpper";
import { action, computed, observable } from "mobx";
import { Modules } from "config";
import BulkExemption, { BulkExemptionValidationRules } from "entities/bulkExemption";
import type ExemptionRepository from "repositories/exemptionRepository";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import { attachAutomaticValidator, attachManualValidator, attachValidatorAggregator } from "utils/validatorAggregator";
import BulkExemptionPreviewResponse from "entities/bulkExemptionPreviewResponse";
import BulkExemptionCancellationPreviewResponse from "entities/bulkExemptionCancellationPreviewResponse";
import type EnumService from "services/enumService";
import type BulkExemptionPreviewItemResponse from "entities/bulkExemptionPreviewItemResponse";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import { emptyValue } from "utils/emptyValue";

export default class BulkExemptionsViewModel extends ScreenBase {
  busyWatcher = new BusyWatcher();
  @observable item = new BulkExemption();
  @observable fileResponse: any = undefined;
  @observable previewResponse: BulkExemptionPreviewResponse;
  @observable cancellationPreviewResponse: BulkExemptionCancellationPreviewResponse;
  @observable isCancelation = false;
  @observable loading = false;
  @observable bulkExemptionStep = 1;
  @observable error?: string;
  @observable private dummy = 0;

  constructor(
    private localizationService: LocalizationService,
    private repository: ExemptionRepository,
    private optionsService: OptionsService,
    private enumService: EnumService
  ) {
    super();
  }

  protected onInitialize(): Promise<any> | void {
    attachValidatorAggregator(this, [
      attachAutomaticValidator(this.item, BulkExemptionValidationRules),
      attachAutomaticValidator(this.item, { reason: { requiredIf: { condition: () => !this.isCancelation } } }),
    ]);
    attachManualValidator(this);
    return super.onInitialize();
  }

  @computed
  get officialRecordId() {
    return this.item.officialRecordId;
  }

  set officialRecordId(recordNumber: string) {
    this.item.officialRecordId = toUpper(recordNumber);
  }

  @computed
  get cancellationItems() {
    return this.cancellationPreviewResponse?.notableItems;
  }

  @computed
  get creationItems() {
    return this.previewResponse?.notableItems;
  }

  @computed
  get officialRecordIdError() {
    return getValidationMessage(this.item, "officialRecordId");
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  onBack() {
    this.bulkExemptionStep = this.bulkExemptionStep > 1 ? this.bulkExemptionStep - 1 : 1;
  }

  @bound
  onFileDownloadClick() {
    download(
      this.fileResponse.data,
      this.item.excelFile?.name,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
    this.requestClose();
  }

  @bound
  onTryAgainClick() {
    this.error = undefined;
  }

  @bound
  onDrop(files: File[]) {
    this.item.excelFile = files[0];
  }

  @bound
  onRemoveFileClick() {
    removeError(this, "item");
    this.item.excelFile = undefined;
    this.dummy++;
  }

  @bound
  @watchBusy
  async onSubmit() {
    if (validate(this)) {
      try {
        if (this.item.reason) {
          this.item.exemptionReasonId = this.getExemptionReasonId(this.item.reason);
        }
        if (!this.isCancelation) {
          await this.bulkCreate();
        } else {
          await this.bulkTerminate();
        }
      } catch (e: any) {}
    }
  }

  @bound
  @watchBusy
  async bulkCreate() {
    this.loading = true;
    if (!this.item.reason) {
      this.item.exemptionReasonId = undefined;
    }
    try {
      if (this.bulkExemptionStep === 1) {
        this.previewResponse = (await this.repository.bulkCreatePreview(this.item)) as unknown as BulkExemptionPreviewResponse;
        if (this.previewResponse.notableItems.length > 0 || this.previewResponse.rejectionReason) {
          this.bulkExemptionStep = 2;
        } else {
          this.fileResponse = await this.repository.bulkCreate(this.item);
        }
      } else {
        this.fileResponse = await this.repository.bulkCreate(this.item);
      }
      this.loading = false;
    } catch (e: any) {
      this.loading = false;
      if (e.response.status === 400) {
        addError(this, "item", this.translate("invalidFile"));
        validate(this);
        this.dummy++;
      } else {
        this.error = this.translate("genericError");
      }
    }
  }

  @bound
  @watchBusy
  async bulkTerminate() {
    this.loading = true;
    if (!this.item.reason) {
      this.item.exemptionReasonId = undefined;
    }
    try {
      if (this.bulkExemptionStep === 1) {
        this.cancellationPreviewResponse = (await this.repository.bulkTerminatePreview(
          this.item
        )) as unknown as BulkExemptionCancellationPreviewResponse;

        if (this.cancellationPreviewResponse.notableItems.length > 0) {
          this.bulkExemptionStep = 2;
        } else {
          this.fileResponse = await this.repository.bulkTerminate(this.item);
        }
      } else {
        this.fileResponse = await this.repository.bulkTerminate(this.item);
      }
      this.loading = false;
    } catch (e: any) {
      this.loading = false;
      if (e.response.status === 400) {
        addError(this, "item", this.translate("invalidFile"));
        validate(this);
        this.dummy++;
      } else {
        this.error = this.translate("genericError");
      }
    }
  }

  @computed
  get exemptionReasonOptions() {
    return this.optionsService.enumOptions("notifiable_exemption_reasons", (item: any) => ({
      id: item.id,
      label: item.description,
    }));
  }

  @bound
  getExemptionReasonId(exemptionReason: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === exemptionReason)?.id ?? emptyValue;
  }

  @computed
  get fileError() {
    void this.dummy;
    return getValidationMessage(this, "item") ?? getValidationMessage(this.item, "excelFile");
  }

  @action.bound
  setCancelation(isCancelation: boolean) {
    this.isCancelation = isCancelation;
  }

  translate(code: string) {
    return this.localizationService.translateModule(Modules.callCenter, `bulkExemptions.${code}`);
  }

  translateAttribute(attribute: keyof BulkExemption) {
    return this.localizationService.translateAttribute("bulkExemption", attribute);
  }

  translateCancellationError(code: string) {
    return this.localizationService.translateModule(Modules.callCenter, `bulkExemptions.cancellationError.${code.toString()}`);
  }

  translateWarningReason(code: string) {
    return this.localizationService.translateModule(Modules.callCenter, `bulkExemptions.warningReason.${code.toString()}`);
  }
  translateCreationError(code: string) {
    return this.localizationService.translateModule(Modules.callCenter, `bulkExemptions.creationError.${code.toString()}`);
  }
  translatePreviewAttribute(attribute: keyof BulkExemptionPreviewItemResponse) {
    return this.localizationService.translateAttribute("bulkExemptionPreviewItem", attribute);
  }

  translateClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }
}
